import React, { useEffect, useId, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/config";
import {
  getCapturedUtm,
  clearCapturedUtm,
} from "../../utils/captureUtm";

// /apply/:jobId — public application form. Standalone (no AppShell).
// Sends UTM block alongside the application; backend infers source_channel.
//
// Flow:
//   GET /job-posting/api/public/jobs/{jobId}    fetch summary + branding
//   POST /job-posting/api/public/applications   submit
//   409 → duplicate (keep form populated, show inline notice)
//
// Resume upload, screening questions, and reCAPTCHA are explicitly out of
// scope this session — see plan-doc #5 follow-ups.

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  linkedinUrl: "",
  coverLetter: "",
  consentDataProcessing: false,
  consentAiScoring: false,
};

export default function Apply() {
  const { jobId } = useParams();

  const [job, setJob] = useState(null);
  const [branding, setBranding] = useState({ primaryColor: "#7B5AC4", name: "Ample Hire" });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(null); // { applicationId, firstName, email, duplicate? }

  useEffect(() => {
    document.title = "Apply - Ample Hire";
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    axios
      .get(`${baseUrl}/job-posting/api/public/jobs/${jobId}`)
      .then((r) => {
        if (cancelled) return;
        setJob(r.data?.job || null);
        setBranding((b) => ({ ...b, ...(r.data?.branding || {}) }));
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        setLoadError(
          status === 404
            ? "This job is not accepting applications, or the link is no longer valid."
            : "Couldn't load the position. Please try again later."
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId]);

  const validationError = useMemo(() => {
    if (!form.firstName.trim()) return "First name is required";
    if (!form.email.trim() || !EMAIL_RE.test(form.email.trim())) return "Valid email is required";
    if (!form.phone.trim()) return "Phone is required";
    if (!form.consentDataProcessing) return "Please consent to data processing to continue";
    return null;
  }, [form]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (validationError || submitting) {
      setSubmitError(validationError);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const utm = getCapturedUtm();
      const body = {
        jobId: Number(jobId),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() || null,
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        linkedinUrl: form.linkedinUrl.trim() || null,
        coverLetter: form.coverLetter.trim() || null,
        consentDataProcessing: form.consentDataProcessing,
        consentAiScoring: form.consentAiScoring,
        ...utm,
      };
      const r = await axios.post(`${baseUrl}/job-posting/api/public/applications`, body);
      clearCapturedUtm();
      setSubmitted({
        applicationId: r.data?.applicationId,
        firstName: r.data?.firstName || form.firstName,
        email: r.data?.email || form.email,
        duplicate: false,
      });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        // Duplicate — friendly, non-blocking message. Keep the form filled.
        setSubmitted({
          applicationId: e.response.data?.applicationId,
          firstName: form.firstName,
          email: form.email,
          duplicate: true,
          message: e.response.data?.message || "You've already applied to this position.",
        });
      } else {
        setSubmitError(
          e?.response?.data?.error || e?.message || "Something went wrong. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Shell branding={branding}><div className="p-10 text-center text-vf-muted">Loading…</div></Shell>;
  }
  if (loadError) {
    return (
      <Shell branding={branding}>
        <div className="bg-white border border-slate-200 rounded-xl p-8 max-w-xl mx-auto text-center">
          <div className="text-3xl mb-3">📭</div>
          <div className="font-semibold text-vf-base mb-2">{loadError}</div>
          <Link to="/careers/ample" className="text-sm text-vf-primary hover:underline">View other roles →</Link>
        </div>
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell branding={branding}>
        <Confirmation
          branding={branding}
          firstName={submitted.firstName}
          email={submitted.email}
          duplicate={submitted.duplicate}
          message={submitted.message}
          jobTitle={job?.title}
          onApplyAgain={() => { setSubmitted(null); setForm(EMPTY_FORM); }}
        />
      </Shell>
    );
  }

  return (
    <Shell branding={branding}>
      <div className="max-w-2xl mx-auto">
        {/* Job summary card */}
        <div
          className="rounded-xl p-5 mb-5 text-white"
          style={{ background: branding.primaryColor || "#7B5AC4" }}
        >
          <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Apply for</div>
          <div className="text-2xl font-bold">{job?.title || `Job #${jobId}`}</div>
          <div className="mt-2 text-sm opacity-90 flex flex-wrap gap-3">
            {job?.hiringType && <Pill>{job.hiringType}</Pill>}
            {Array.isArray(job?.locations) && job.locations.length > 0 && (
              <Pill>{job.locations.slice(0, 3).join(", ")}</Pill>
            )}
            {(job?.minimumExperience || job?.maximumExperience) && (
              <Pill>{job.minimumExperience || "0"}–{job.maximumExperience || "?"} yrs</Pill>
            )}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <SectionTitle>Your info</SectionTitle>
          <Row>
            <Field label="First name" required>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-vf-primary"
              />
            </Field>
            <Field label="Last name">
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-vf-primary"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Email" required>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-vf-primary"
              />
            </Field>
            <Field label="Phone" required>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-vf-primary"
              />
            </Field>
          </Row>
          <Field label="LinkedIn URL">
            <input
              type="url"
              value={form.linkedinUrl}
              onChange={(e) => update("linkedinUrl", e.target.value)}
              placeholder="https://linkedin.com/in/yourname"
              className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-vf-primary"
            />
          </Field>

          <SectionTitle>Cover letter (optional)</SectionTitle>
          <textarea
            value={form.coverLetter}
            onChange={(e) => update("coverLetter", e.target.value)}
            rows={5}
            placeholder="Why are you a good fit?"
            className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-vf-primary"
          />

          <div className="border-t border-slate-100 pt-4 space-y-1">
            <label className="flex items-start gap-3 text-sm text-vf-base cursor-pointer py-2 min-h-[44px]">
              <input
                type="checkbox"
                checked={form.consentDataProcessing}
                onChange={(e) => update("consentDataProcessing", e.target.checked)}
                className="mt-0.5 w-5 h-5 flex-shrink-0"
              />
              <span>
                <b>I consent</b> to {branding.name || "the company"} processing my application data for hiring purposes.
                <span className="text-red-500"> *<span className="sr-only">(required)</span></span>
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-vf-base cursor-pointer py-2 min-h-[44px]">
              <input
                type="checkbox"
                checked={form.consentAiScoring}
                onChange={(e) => update("consentAiScoring", e.target.checked)}
                className="mt-0.5 w-5 h-5 flex-shrink-0"
              />
              <span>
                I consent to AI-assisted scoring of any interview I take as part of this application (optional).
              </span>
            </label>
          </div>

          {submitError && (
            <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700">{submitError}</div>
          )}

          <button
            onClick={submit}
            disabled={submitting || !!validationError}
            className="w-full px-4 py-3 rounded text-white text-base font-semibold min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
            style={{ background: branding.primaryColor || "#7B5AC4" }}
          >
            {submitting ? "Submitting…" : "Submit application"}
          </button>
          {validationError && (
            <div className="text-[11px] text-vf-muted text-center">{validationError}</div>
          )}
        </div>
      </div>
    </Shell>
  );
}

// ── shell + sub-components ────────────────────────────────────────────────
function Shell({ branding, children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-3">
          {branding?.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.name || "Logo"} className="h-8" />
          ) : (
            <div className="font-bold text-vf-base">{branding?.name || "Ample Hire"}</div>
          )}
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">{children}</main>
      <footer className="py-6 text-center text-xs text-slate-400">
        Powered by Ample Hire
      </footer>
    </div>
  );
}

function Confirmation({ branding, firstName, email, duplicate, message, jobTitle, onApplyAgain }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-8 max-w-xl mx-auto text-center">
      <div
        className="mx-auto w-14 h-14 rounded-full flex items-center justify-center text-white text-2xl mb-4"
        style={{ background: branding.primaryColor || "#7B5AC4" }}
      >
        {duplicate ? "!" : "✓"}
      </div>
      <h2 className="text-xl font-semibold text-vf-base mb-2">
        {duplicate
          ? "You've already applied"
          : `Application received${firstName ? `, ${firstName}` : ""}`}
      </h2>
      <p className="text-sm text-vf-muted mb-5">
        {duplicate
          ? message || "We have your earlier application on file. We'll be in touch."
          : (
              <>We've sent a confirmation to <b>{email}</b>{jobTitle ? <> for <b>{jobTitle}</b></> : null}. You'll hear from us soon.</>
            )}
      </p>
      <div className="flex justify-center gap-2">
        <Link to="/careers/ample" className="px-3 py-1.5 rounded border border-slate-300 text-xs">
          View other roles →
        </Link>
        {duplicate && (
          <button onClick={onApplyAgain} className="px-3 py-1.5 rounded border border-slate-300 text-xs">
            Use a different email
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}
function SectionTitle({ children }) {
  return <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">{children}</div>;
}
function Field({ label, required, children }) {
  // Generate a stable id and pass it to the child via cloneElement so the
  // <label> ↔ <input> association is automatic (closes the jsx-a11y warning
  // and improves screen-reader UX). Callers don't have to remember to id
  // their inputs.
  const id = useId();
  const child = React.Children.only(children);
  const labelled = React.cloneElement(child, {
    id,
    "aria-required": required || undefined,
  });
  return (
    <div>
      <label htmlFor={id} className="block text-xs text-vf-muted mb-1">
        {label}
        {required && <span className="text-red-500"> *<span className="sr-only">(required)</span></span>}
      </label>
      {labelled}
    </div>
  );
}
function Pill({ children }) {
  return <span className="px-2 py-0.5 rounded-full bg-white/20 text-xs">{children}</span>;
}
